import { CreateTestDialog } from "@/components/car-test/CreateTestDialog";
import { VehicleTest } from "@/types/vehicleTest";
import { CarPostAnAdWidgetProps } from "@/components/car-post-ad/CarPostAnAdWidget";
import { useTest } from "@/hooks/useTest";
import { toast } from "sonner";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";

interface EditDialogProps {
  test: VehicleTest | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  activeTab: string;
  onTabChange: (value: string) => void;
  listingId?: string;
  listingType?: "auction" | "direct-purchase";
}

export const EditTestDialog = ({
  test,
  isOpen,
  onOpenChange,
  activeTab,
  onTabChange,
  listingId,
  listingType,
}: EditDialogProps) => {
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  // Fetch data for updating tests
  const { updateTest } = useTest();

  if (!test) return null;

  // Use test data directly since it's already in the correct format
  const initialData: CarPostAnAdWidgetProps["initialData"] = {
    id: test.id, // Add test ID for image updates
    regNumber: test.vehicleDetails?.regNumber,
    vehicleDetails: test.vehicleDetails,
    vehicleTestData: test.vehicleTestData,
    images: test.images,
    status: test.status,
    price: test.price,
    comments: test.comments,
    vatCar: test.vatCar,
    initialStep: 3, // Start at the Bilder step to show all data
    purchasePrice: test.purchasePrice,
  };

  const handleTestSaved = async (updatedTest: VehicleTest) => {
    if (!listingId) return;

    try {
      // Extract the actual test data from the response
      const testData =
        (updatedTest as { data?: VehicleTest }).data || updatedTest;

      // Update the test using the test service
      await updateTest({
        testId: test.id,
        updateData: {
          vehicleName: testData.vehicleName,
          vehicleDetails: testData.vehicleDetails,
          vehicleTestData: testData.vehicleTestData,
          images: testData.images,
          price: testData.price,
          comments: testData.comments,
          vatCar: testData.vatCar,
          customerId: testData.customerId,
        },
      });

      toast.success(
        ct.messages.testUpdated || "Fordonstest uppdaterat framgångsrikt!",
      );

      onOpenChange(false);
    } catch (error) {
      toast.error(
        ct.messages.testUpdateError ||
          "Ett fel uppstod vid uppdatering av fordonstestet",
      );
    }
  };

  // Use test data directly for the title

  return (
    <CreateTestDialog
      open={isOpen}
      onOpenChange={onOpenChange}
      initialData={initialData}
      isEditMode={true}
      title={ct.dialogs.edit.title.replace(
        "{vehicleName}",
        test.vehicleDetails?.regNumber || "",
      )}
      onTestSaved={handleTestSaved}
    />
  );
};
